import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import type { ItineraryItemType, Trip } from '../../types/client';
import type { ForecastDay } from '../../types/weather';
import { tripDetailEn } from '../../content/trips.en';
import { weatherEn } from '../../content/weather.en';
import { useSubscriptionFeature } from '../../contexts/SubscriptionContext';
import {
  isCityVisitQueuedForAiPlan,
  subscribeAiPlanJobsChanged,
} from '../../services/aiPlanJobService';
import {
  enumerateStayDays,
  formatCardWeekdayDate,
  itemTypesForCityDay,
  portTimeLineForStayDay,
  type TripDayCardModel,
} from '../../utils/tripDayCards';
import {
  initialAIPlanButtonPhase,
  isPlanInFlight,
  resolveAIPlanButtonState,
} from '../../utils/aiPlanButtonStateResolver';
import { formatTempRange, resolveTripTemperatureUnit } from '../../utils/temperatureUnit';
import { isPastVisitDate } from '../../utils/preferenceFilters';
import WeatherConditionIcon from '../weather/WeatherConditionIcon';
import CountryInfoDrawer from '../CountryInfo/CountryInfoDrawer';
import { FerryIcon, TypeIcon } from '../icons/TripIcons';
import styles from './CityVisitCard.module.css';

interface CityVisitCardProps {
  tripId: string;
  card: TripDayCardModel;
  showPlanIcons: boolean;
  canPlan: boolean;
  /** Weather keyed by YYYY-MM-DD for this city visit. */
  weatherByDate?: Record<string, ForecastDay | null>;
  temperatureUnit?: string | null;
  weatherLoading?: boolean;
  trip?: Trip | null;
  /** When true, omit civil days that ended before today (iOS hidePastDays). */
  hidePastDays?: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  docked: tripDetailEn.docked,
  tendered: tripDetailEn.tendered,
  cruising: tripDetailEn.cruising,
};

const IN_FLIGHT_TICK_MS = 30_000;

const CityVisitCard: React.FC<CityVisitCardProps> = ({
  tripId,
  card,
  showPlanIcons,
  canPlan,
  weatherByDate = {},
  temperatureUnit,
  weatherLoading = false,
  trip = null,
  hidePastDays = false,
}) => {
  const navigate = useNavigate();
  const includesAIPlan = useSubscriptionFeature('aiPlan');
  const [countryInfoOpen, setCountryInfoOpen] = React.useState(false);
  const [queueTick, setQueueTick] = useState(0);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const unit = resolveTripTemperatureUnit({ temperatureUnit });

  const stayDays = useMemo(() => {
    const days = enumerateStayDays(card.arrivalDate, card.departureDate);
    if (!hidePastDays) return days;
    return days.filter((ymd) => !isPastVisitDate(ymd, ymd));
  }, [card.arrivalDate, card.departureDate, hidePastDays]);

  const status =
    card.isCruisePort && card.cruisePortStatus
      ? STATUS_LABEL[card.cruisePortStatus] ?? tripDetailEn.docked
      : card.isCruisePort
        ? tripDetailEn.docked
        : null;

  const cityMeta = trip?.cityVisitMeta?.[card.cityId];
  const planReady = Boolean(trip?.aiPlanDataByCity?.[card.cityId]);

  useEffect(() => {
    return subscribeAiPlanJobsChanged((changedTripId) => {
      if (changedTripId.trim().toLowerCase() !== tripId.trim().toLowerCase()) return;
      setQueueTick((n) => n + 1);
    });
  }, [tripId]);

  const aiPlanButton = useMemo(() => {
    const isQueued = isCityVisitQueuedForAiPlan(tripId, card.cityId);
    const inputs = {
      hasPlanData: planReady,
      aiStatus: cityMeta?.aiStatus,
      aiJobId: cityMeta?.aiJobId,
      aiJobStartedAt: cityMeta?.aiJobStartedAt,
      isQueued,
    };
    const phase = initialAIPlanButtonPhase(inputs, new Date(nowTick));
    return resolveAIPlanButtonState(inputs, phase, new Date(nowTick), {
      ready: tripDetailEn.aiPlan,
      creating: tripDetailEn.aiPlanCreating,
      parsing: tripDetailEn.aiPlanParsing,
      formatting: tripDetailEn.aiPlanFormatting,
    });
  }, [
    tripId,
    card.cityId,
    planReady,
    cityMeta?.aiStatus,
    cityMeta?.aiJobId,
    cityMeta?.aiJobStartedAt,
    queueTick,
    nowTick,
  ]);

  useEffect(() => {
    const isQueued = isCityVisitQueuedForAiPlan(tripId, card.cityId);
    const inFlight = isPlanInFlight(
      {
        hasPlanData: planReady,
        aiStatus: cityMeta?.aiStatus,
        aiJobId: cityMeta?.aiJobId,
        aiJobStartedAt: cityMeta?.aiJobStartedAt,
        isQueued,
      },
      new Date(nowTick)
    );
    if (!inFlight) return undefined;
    const timer = window.setInterval(() => {
      setNowTick(Date.now());
    }, IN_FLIGHT_TICK_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [
    tripId,
    card.cityId,
    planReady,
    cityMeta?.aiStatus,
    cityMeta?.aiJobId,
    cityMeta?.aiJobStartedAt,
    queueTick,
    nowTick,
  ]);

  if (stayDays.length === 0 && !card.arrivalDate) {
    // Visit with no dates — still show header so the city is visible.
  } else if (stayDays.length === 0) {
    return null;
  }

  const openDay = (dayYmd: string): void => {
    if (!canPlan) return;
    navigate(
      `/trips/${tripId}/days/${dayYmd}?cityVisitId=${encodeURIComponent(card.cityId)}`
    );
  };

  const transportVisible = (dayYmd: string): boolean => {
    if (trip?.tripType === 'cruise') return card.showFlightIcon;
    return true;
  };

  const dayItemTypes = (dayYmd: string): ItineraryItemType[] => {
    if (!trip) return [];
    return itemTypesForCityDay(trip, card.cityId, dayYmd);
  };

  const planTypesForDay = (dayYmd: string): ItineraryItemType[] => {
    const types = dayItemTypes(dayYmd);
    if (!showPlanIcons) return types;
    return (['transport', 'activity', 'meal', 'accommodation'] as ItineraryItemType[]).filter(
      (type) => {
        if (type !== 'transport') return true;
        return transportVisible(dayYmd);
      }
    );
  };

  return (
    <>
      <div className={styles.card}>
        <div className={styles.topRow}>
          <div className={styles.titleGroup}>
            <span className={styles.cityName}>{card.cityName}</span>
            {!card.isAtSea ? (
              <button
                type="button"
                className={styles.infoIcon}
                title={tripDetailEn.countryInfo ?? 'City info'}
                aria-label={tripDetailEn.countryInfo ?? 'City info'}
                onClick={() => setCountryInfoOpen(true)}
              >
                ⓘ
              </button>
            ) : null}
            {status ? (
              <span
                className={`${styles.statusBadge} ${
                  card.cruisePortStatus === 'tendered'
                    ? styles.statusTendered
                    : card.cruisePortStatus === 'cruising'
                      ? styles.statusCruising
                      : styles.statusDocked
                }`}
              >
                <FerryIcon size={14} />
                {status}
              </span>
            ) : null}
          </div>
          <div className={styles.trailing}>
            {showPlanIcons && !card.isAtSea && canPlan && includesAIPlan ? (
              aiPlanButton.isEnabled ? (
                <Link
                  to={`/trips/${tripId}/plan?cityVisitId=${encodeURIComponent(card.cityId)}`}
                  className={styles.planButton}
                >
                  {aiPlanButton.title}
                </Link>
              ) : (
                <span
                  className={`${styles.planButton} ${styles.planButtonDisabled}`}
                  aria-disabled="true"
                >
                  <Spin size="small" /> {aiPlanButton.title}
                </span>
              )
            ) : null}
          </div>
        </div>

        <div className={styles.dayRows}>
          {(stayDays.length > 0 ? stayDays : [card.arrivalDate].filter(Boolean) as string[]).map(
            (dayYmd) => {
              const dateLabel = formatCardWeekdayDate(dayYmd);
              const timeLine = portTimeLineForStayDay({
                dayYmd,
                arrivalDate: card.arrivalDate,
                departureDate: card.departureDate,
                portArrivalHm: card.portArrivalHm,
                portDepartureHm: card.portDepartureHm,
              });
              const weather = weatherByDate[dayYmd] ?? null;
              const dayHref = canPlan
                ? `/trips/${tripId}/days/${dayYmd}?cityVisitId=${encodeURIComponent(card.cityId)}`
                : undefined;
              const types = planTypesForDay(dayYmd);
              const activeTypes = dayItemTypes(dayYmd);

              return (
                <div
                  key={dayYmd}
                  className={`${styles.dayRow} ${dayHref ? styles.dayRowClickable : ''}`}
                  role={dayHref ? 'link' : undefined}
                  tabIndex={dayHref ? 0 : undefined}
                  onClick={dayHref ? () => openDay(dayYmd) : undefined}
                  onKeyDown={
                    dayHref
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openDay(dayYmd);
                          }
                        }
                      : undefined
                  }
                  aria-label={
                    dayHref
                      ? `${card.cityName}, ${dateLabel ?? dayYmd}. ${tripDetailEn.openDay}`
                      : `${card.cityName}, ${dateLabel ?? dayYmd}`
                  }
                >
                  <div className={styles.dayRowMain}>
                    <div className={styles.metaRow}>
                      {dateLabel ? <span className={styles.date}>{dateLabel}</span> : null}
                      {timeLine ? <span className={styles.time}>{timeLine}</span> : null}
                    </div>
                    {showPlanIcons || activeTypes.length > 0 ? (
                      <div className={styles.iconsRow} aria-hidden>
                        {types.map((type) => (
                          <span
                            key={type}
                            className={`${styles.typeIcon} ${
                              activeTypes.includes(type) ? styles.typeIconActive : ''
                            }`}
                          >
                            <TypeIcon type={type} size={18} />
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className={styles.dayRowTrailing}>
                    {!card.isAtSea ? (
                      <div className={styles.weatherCompact} aria-label="Weather">
                        {weatherLoading && !(dayYmd in weatherByDate) ? (
                          <span className={styles.weatherLoading}>{weatherEn.loading}</span>
                        ) : weather ? (
                          <>
                            <WeatherConditionIcon
                              iconName={weather.iconName}
                              dimmed={weather.isHistorical}
                              size={16}
                            />
                            <span className={styles.weatherRange}>
                              {formatTempRange(weather.highTempC, weather.lowTempC, unit)}
                            </span>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                    {dayHref ? (
                      <span className={styles.chevron} aria-hidden>
                        ›
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            }
          )}
        </div>
      </div>

      <CountryInfoDrawer
        open={countryInfoOpen}
        onClose={() => setCountryInfoOpen(false)}
        cityName={card.cityName}
        countryISO2={card.countryISO2}
        countryName={card.countryName}
        currencyCode={card.currencyCode}
        timeZoneIdentifier={card.timeZoneIdentifier}
        tripStartDate={trip?.startDate}
        cityArrivalDate={card.arrivalDate}
        tripCurrency={trip?.currency}
        tripHomeTimeZone={trip?.homeTimeZone}
        isCruisePort={card.isCruisePort}
        coords={trip?.cityCoords?.[card.cityId] ?? null}
        dockNumber={card.dockNumber}
        portArrivalHm={card.portArrivalHm}
        portDepartureHm={card.portDepartureHm}
      />
    </>
  );
};

export default CityVisitCard;
